/**
 * PATH: packages/ui/src/kanban/components/card-detail/emailRowMapping.ts
 * FILE_ID: UI-KANBAN-EMAIL-ROW-MAPPING-001
 * PURPOSE: Map the kanban email adapter's preview/rail shape onto the inbox
 *          <EmailRow> `EmailMessage` contract, plus the neutral category visuals
 *          and the star/select swallow used on the card-context rows.
 * WHY:     Both the card's linked-email rows (LinkedEmailRow) and the
 *          attach-an-email search results (CardLinkedEmailSection) render the
 *          SAME inbox <EmailRow> primitive (the consistency operators asked for
 *          on 2026-06-22). Sharing the mapping here keeps a single source of
 *          truth and avoids an import cycle between those two components.
 * CONSTRAINT: @quotech/ui cannot import the email-hub's CATEGORY_CONFIG (package
 *          dependency cycle), so the card-context row shows a muted "Email" dot
 *          rather than the inbox's coloured category hue.
 * RELATED:
 *   - components/email/EmailRow.tsx: the universal inbox row this feeds.
 *   - card-detail/LinkedEmailRow.tsx, card-detail/CardLinkedEmailSection.tsx.
 */
import type { MouseEvent } from 'react'
import { Mail } from 'lucide-react'

import type { EmailRowCategoryConfig } from '../../../components/email/EmailRow'
import type { EmailMessage } from '../../../components/email/types'
import type { KanbanEmailPreview, KanbanEmailRailItem } from '../../api'

/** Neutral category visuals. The inbox computes its coloured category dot from
 *  email-hub's CATEGORY_CONFIG, which is unreachable from @quotech/ui — so the
 *  card-context row shows a muted "Email" dot. Layout/shape stay identical to
 *  the inbox row (the consistency the operator asked for); only the category
 *  hue is generic here. */
export const NEUTRAL_CATEGORY: EmailRowCategoryConfig = {
  icon: Mail,
  dot: 'bg-neutral-300 dark:bg-zinc-600',
  label: 'Email',
  dotIsMuted: true,
}

/** Split an RFC "Name <addr@host>" From line into the name + address pair the
 *  <EmailRow> identity column expects. Falls back gracefully for a bare
 *  address or a bare display name. */
export function splitFromLine(from: string | undefined): { name: string; address: string } {
  const s = (from ?? '').trim()
  const angle = s.match(/^\s*"?([^"<]*?)"?\s*<([^>]+)>\s*$/)
  if (angle) {
    const address = angle[2].trim()
    const name = angle[1].trim()
    return { name: name || address, address }
  }
  if (s.includes('@')) return { name: s, address: s }
  return { name: s, address: '' }
}

/** Map the kanban email adapter's preview/rail shape onto the <EmailRow>
 *  EmailMessage contract. Read-state/star aren't carried on the kanban path,
 *  so they default to the neutral (read, unstarred) presentation. */
export function toEmailMessage(p: KanbanEmailPreview | KanbanEmailRailItem): EmailMessage {
  const { name, address } = splitFromLine(p.from)
  const attachmentCount = p.attachment_count ?? 0
  return {
    id: p.id,
    from_address: address,
    from_name: name,
    subject: p.subject ?? '',
    body_preview: p.body_preview ?? '',
    received_at: p.received_at,
    is_read: true,
    is_starred: false,
    has_attachments: attachmentCount > 0,
    attachment_count: attachmentCount,
    category: (p as KanbanEmailRailItem).category,
  }
}

/** A no-op that swallows the click so the (inert, no star API on the kanban
 *  adapter) star / select buttons never bubble up to the row's open handler. */
export function swallow(e: MouseEvent<HTMLButtonElement>) {
  e.stopPropagation()
}
